import { EvolysProvider, CommandPalette } from "@evolys/ui";
import { AuthProvider, usePermissions } from "@evolys/auth";
import { Notifications } from "@mantine/notifications";
import { SpotlightActionData } from "@mantine/spotlight";
import { useNavigate } from "react-router-dom";
import {
  IconHome,
  IconUsers,
  IconUserPlus,
  IconTestPipe,
  IconClipboardList,
  IconMicroscope,
} from "@tabler/icons-react";
import { isOperationsWorkspace } from "@/lib/workspaceMode";

const API_BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:5000";

function useLabSpotlightActions(): SpotlightActionData[] {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const actions: Array<
    SpotlightActionData & { permission?: string }
  > = [
    {
      id: "dashboard",
      label: "Dashboard",
      description: "Go to the lab dashboard",
      leftSection: <IconHome size={20} stroke={1.5} />,
      onClick: () => navigate("/"),
    },
    {
      id: "samples",
      label: isOperationsWorkspace
        ? "Specimens & accessioning"
        : "Bacteriology specimens",
      description: isOperationsWorkspace
        ? "Collect, receive, label and route specimens"
        : "Open the bacteriology specimen queue",
      leftSection: <IconTestPipe size={20} stroke={1.5} />,
      onClick: () => navigate("/samples"),
      permission: "specimens.view",
    },
  ];

  if (isOperationsWorkspace) {
    actions.push(
      {
        id: "patients",
        label: "Patients",
        description: "Search and manage patients",
        leftSection: <IconUsers size={20} stroke={1.5} />,
        onClick: () => navigate("/patients"),
        permission: "patients.search",
      },
      {
        id: "create-patient",
        label: "Register patient",
        description: "Create a new patient record",
        leftSection: <IconUserPlus size={20} stroke={1.5} />,
        onClick: () => navigate("/patients/create"),
        permission: "patients.create",
      },
      {
        id: "create-order",
        label: "Create order",
        description: "Order tests across laboratory departments",
        leftSection: <IconClipboardList size={20} stroke={1.5} />,
        onClick: () => navigate("/orders/new"),
        permission: "orders.create",
      },
    );
  } else {
    actions.push({
      id: "bench-read",
      label: "Bench read",
      description: "Continue bacteriology culture work",
      leftSection: <IconMicroscope size={20} stroke={1.5} />,
      onClick: () => navigate("/bench-read"),
      permission: "bench_read.access",
    });
  }

  return actions.filter(
    (action) =>
      !action.permission || hasPermission(action.permission),
  );
}

function AuthenticatedWorkspace({
  children,
}: {
  children: React.ReactNode;
}) {
  const actions = useLabSpotlightActions();

  return (
    <>
      <CommandPalette actions={actions} />
      {children}
    </>
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <EvolysProvider>
      <Notifications position="top-right" />
      <AuthProvider apiBaseUrl={API_BASE_URL}>
        <AuthenticatedWorkspace>{children}</AuthenticatedWorkspace>
      </AuthProvider>
    </EvolysProvider>
  );
}
